import {
    ChangeDetectionStrategy,
    Component,
    ViewChild,
    inject
} from "@angular/core";

import {
    MatSidenav, MatSidenavContainer, MatSidenavContent
} from "@angular/material/sidenav";

import {
    MatIconButton
} from "@angular/material/button";
import {
    MatMenuTrigger, MatMenu, MatMenuItem
} from "@angular/material/menu";
import { MatIcon } from "@angular/material/icon";
import { MatDivider } from "@angular/material/divider";
import {
    MatNavList, MatListItem, MatListItemIcon
} from "@angular/material/list";
import { MatTooltip } from "@angular/material/tooltip";
import { CdkScrollable } from "@angular/cdk/scrolling";
import {
    RouterLinkActive, RouterLink, RouterOutlet
} from "@angular/router";
import { MatLine } from "@angular/material/core";
import {
    map, Observable
} from "rxjs";
import { AsyncPipe } from "@angular/common";
import { ISidenavLink } from "../../interfaces/sidenav-link";
import { ArrayUtil } from "@shopliftr/common-js/data";
import { IAppLinks } from "../../interfaces/app-links";
import { SupportRequestDialogService } from "../../../services/support/support-request-dialog.service";
import { UserDialogService } from "../../services/user-dialog.service";
import {
    COMMON_APP_SERVICE
} from "../../../application/constants/app-tokens";
import { IAppService } from "../../../application/interfaces/app-service";
import { IInternalApiConfig } from "../../interfaces/internal-api-config";
import { IAuthenticationAppRouterService } from "../../interfaces/authentication-app-router-service";
import { NavigationSource } from "../../../application/enums/navigation-source";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { ProcessingDirective } from "../../../application/directives/processing/processing.directive";

@Component({
    selector: "mgd-sidenav",
    templateUrl: "./sidenav.component.html",
    styleUrls: ["./sidenav.component.scss"],
    imports: [MatSidenavContainer,
        MatSidenav,
        MatIconButton,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatMenuItem,
        MatDivider,
        MatNavList,
        MatListItem,
        MatTooltip,
        RouterLinkActive,
        RouterLink,
        MatListItemIcon,
        MatLine,
        MatSidenavContent,
        RouterOutlet,
        AsyncPipe,
        ButtonDirective,
        ProcessingDirective,
        CdkScrollable
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavComponent {

    @ViewChild(MatSidenav) sidenav: MatSidenav;

    navigationSource = NavigationSource.Sidenav;

    private readonly _app = inject(COMMON_APP_SERVICE) as IAppService<IInternalApiConfig, IAuthenticationAppRouterService>;
    private readonly _supportRequestDialog = inject(SupportRequestDialogService);
    private readonly _userDialog = inject(UserDialogService);

    get version(): string {
        return this._app.config.appVersion;
    }

    get userName$(): Observable<string> {
        return this._app.authService.authenticatedUser$.pipe(
            map((user) => user?.name ?? "")
        );
    }

    get sidenavOpened$(): Observable<boolean> {
        return this._app.fullscreen$.pipe(
            map((fullscreen) => !fullscreen)
        );
    }

    get sidenavLinks$(): Observable<Array<ISidenavLink>> {
        return this._app.authService.authenticatedUser$.pipe(
            map((user) => {
                const links = this._app.appRouter.sidenavLinks;
                if (user && links) {
                    return links.filter(
                        (link) => ArrayUtil.isEmpty(link.permissions)
                            || link.permissions.some((permission) => user.hasPermission(permission))
                    );
                }
                return [];
            })
        );
    }

    get appLinks$(): Observable<Array<IAppLinks>> {
        return this._app.authService.authenticatedUser$.pipe(
            map((user) => {
                const links = this._app.config.appLinks ?? [];
                if (user?.hasRole("sysadmin")) {
                    return links;
                }
                if (user) {
                    return links.filter((link) => link.roles.find((role) => user.hasRole(role)));
                }
                return [];
            })
        );
    }

    /**
     * Open the Update Password dialog.
     */
    onUpdatePasswordClick(): void {

        this._userDialog.openUpdatePassword();
    }

    /**
     * Invoke the Support service to create a JIRA support ticket.
     */
    onRequestSupportClick(event: Event): void {

        event?.preventDefault();
        this._supportRequestDialog.open();
    }

    /**
     * Log the current user out
     */
    onLogoutClick(): void {
        this._app.appRouter.toLogout(this.navigationSource);
    }

    /**
     * Toggle fullscreen mode which controls sidenav visibility
     */
    onSidenavToggleClick(): void {
        if (this._app.fullscreen) {
            this._app.fullscreenOff();
        }
        else {
            this._app.fullscreenOn();
        }
    }
}
